import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Shield, FileText, RefreshCw, Trash2, Menu, X } from 'lucide-react';

/**
 * Legal Policy Page for Superhost CRM
 * Public page accessible at /legal and /privacy-policy
 */
const LegalPolicyPage = () => {
  const [activeSection, setActiveSection] = useState('privacy');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  const sectionRefs = {
    privacy: useRef(null),
    terms: useRef(null),
    refund: useRef(null),
    deletion: useRef(null),
  };

  const sections = [
    { id: 'privacy', label: 'Privacy Policy', icon: Shield },
    { id: 'terms', label: 'Terms & Conditions', icon: FileText },
    { id: 'refund', label: 'Refund Policy', icon: RefreshCw },
    { id: 'deletion', label: 'Data Deletion Policy', icon: Trash2 },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      
      for (const section of sections) {
        const element = sectionRefs[section.id].current;
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = sectionRefs[sectionId].current;
    if (element) {
      const offset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <Helmet>
        <title>Superhost CRM Legal Policies</title>
        <meta name="description" content="Privacy Policy, Terms & Conditions, Refund Policy and Data Deletion Policy for Superhost CRM." />
      </Helmet>

      <div className="min-h-screen" style={{ backgroundColor: '#F8FAFC' }}>
        {/* Navbar */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: '#10B981' }}>
                  S
                </div>
                <span className="font-bold text-xl" style={{ color: '#0F172A' }}>Superhost CRM</span>
              </Link>
              
              <div className="hidden md:flex items-center gap-6">
                <Link to="/" className="text-sm font-medium hover:opacity-80" style={{ color: '#334155' }}>
                  Home
                </Link>
                <Link to="/legal" className="text-sm font-medium" style={{ color: '#10B981' }}>
                  Legal
                </Link>
              </div>

              <button 
                className="md:hidden p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-100 py-4 px-4">
              <Link to="/" className="block py-2 text-sm font-medium" style={{ color: '#334155' }}>
                Home
              </Link>
              <Link to="/legal" className="block py-2 text-sm font-medium" style={{ color: '#10B981' }}>
                Legal
              </Link>
            </div>
          )}
        </nav>

        {/* Hero Section */}
        <section className="pt-24 pb-12 bg-white">
          <div className="max-w-[900px] mx-auto px-4 text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#0F172A', lineHeight: 1.2 }}>
              Superhost CRM Legal Policies
            </h1>
            <p className="text-base sm:text-lg max-w-2xl mx-auto" style={{ color: '#334155', lineHeight: 1.6 }}>
              Privacy Policy, Terms & Conditions, Refund Policy and Data Deletion Policy for Superhost CRM users.
            </p>
          </div>
        </section>

        {/* Sticky Navigation Tabs */}
        <div className="sticky top-16 z-40 bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-[900px] mx-auto px-4">
            <div className="flex overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
              {sections.map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`flex items-center gap-2 px-4 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                      isActive 
                        ? 'border-current' 
                        : 'border-transparent hover:border-gray-300'
                    }`}
                    style={{ 
                      color: isActive ? '#10B981' : '#334155',
                      borderColor: isActive ? '#10B981' : undefined
                    }}
                  >
                    <Icon size={18} />
                    {section.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Policy Content */}
        <main className="py-12 px-4">
          <div 
            className="max-w-[900px] mx-auto bg-white rounded-xl shadow-sm border border-gray-100"
            style={{ lineHeight: 1.6 }}
          >
            <div className="p-6 sm:p-10">
              {/* Introduction */}
              <div className="mb-12 pb-8 border-b border-gray-100">
                <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ color: '#0F172A' }}>
                  Superhost CRM – Legal Policies
                </h2>
                <p className="text-sm font-medium mb-6" style={{ color: '#10B981' }}>
                  Effective Date: 1 January 2026
                </p>
                <p className="mb-4" style={{ color: '#334155' }}>
                  Welcome to Superhost CRM. This page outlines our Privacy Policy, Terms & Conditions, Refund Policy, and Data Deletion Policy. By using our platform, you agree to the policies described below.
                </p>
                <p style={{ color: '#334155' }}>
                  Superhost CRM is a Business-to-Business (B2B) software platform designed for property owners, hotels, and hospitality businesses to manage leads, properties, OTA integrations, and operational data.
                </p>
              </div>

              {/* Privacy Policy Section */}
              <section ref={sectionRefs.privacy} id="privacy" className="mb-12 scroll-mt-40">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: '#ECFDF5' }}>
                    <Shield size={24} style={{ color: '#10B981' }} />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold" style={{ color: '#0F172A' }}>
                    1. Privacy Policy
                  </h2>
                </div>
                
                <p className="mb-6" style={{ color: '#334155' }}>
                  Your privacy is important to us. This Privacy Policy explains how we collect, process, store, and protect your information when you use Superhost CRM.
                </p>

                <h3 className="text-lg font-semibold mb-4" style={{ color: '#0F172A' }}>
                  2. Information We Collect
                </h3>

                <div className="grid sm:grid-cols-2 gap-6 mb-6">
                  <div className="p-4 rounded-lg" style={{ backgroundColor: '#F8FAFC' }}>
                    <h4 className="font-semibold mb-3" style={{ color: '#0F172A' }}>Personal Information</h4>
                    <ul className="space-y-2 text-sm" style={{ color: '#334155' }}>
                      <li>• Name</li>
                      <li>• Phone number</li>
                      <li>• Email address</li>
                      <li>• Business details</li>
                      <li>• Property information</li>
                    </ul>
                  </div>
                  <div className="p-4 rounded-lg" style={{ backgroundColor: '#F8FAFC' }}>
                    <h4 className="font-semibold mb-3" style={{ color: '#0F172A' }}>Account Information</h4>
                    <ul className="space-y-2 text-sm" style={{ color: '#334155' }}>
                      <li>• Login credentials</li>
                      <li>• Account preferences</li>
                      <li>• User activity logs</li>
                      <li>• Role and permission data</li>
                    </ul>
                  </div>
                  <div className="p-4 rounded-lg" style={{ backgroundColor: '#F8FAFC' }}>
                    <h4 className="font-semibold mb-3" style={{ color: '#0F172A' }}>Business Data</h4>
                    <ul className="space-y-2 text-sm" style={{ color: '#334155' }}>
                      <li>• Property and hotel information</li>
                      <li>• OTA listing details</li>
                      <li>• Booking records</li>
                      <li>• Customer contact information</li>
                      <li>• Uploaded documents</li>
                      <li>• Payment and billing data</li>
                    </ul>
                  </div>
                  <div className="p-4 rounded-lg" style={{ backgroundColor: '#F8FAFC' }}>
                    <h4 className="font-semibold mb-3" style={{ color: '#0F172A' }}>Technical Information</h4>
                    <ul className="space-y-2 text-sm" style={{ color: '#334155' }}>
                      <li>• IP address</li>
                      <li>• Device type</li>
                      <li>• Browser type</li>
                      <li>• Session data</li>
                      <li>• Usage analytics</li>
                      <li>• Cookies</li>
                    </ul>
                  </div>
                </div>

                <h3 className="text-lg font-semibold mb-4" style={{ color: '#0F172A' }}>
                  3. How We Use Your Information
                </h3>
                <div className="p-4 rounded-lg mb-6" style={{ backgroundColor: '#F8FAFC' }}>
                  <ul className="grid sm:grid-cols-2 gap-2 text-sm" style={{ color: '#334155' }}>
                    <li>• Provide CRM functionality</li>
                    <li>• Manage accounts</li>
                    <li>• Improve performance</li>
                    <li>• Deliver support</li>
                    <li>• Send notifications</li>
                    <li>• Prevent fraud</li>
                    <li>• Maintain security</li>
                  </ul>
                </div>
                <p className="font-medium mb-6" style={{ color: '#10B981' }}>
                  We do not sell or rent user data.
                </p>

                <h3 className="text-lg font-semibold mb-4" style={{ color: '#0F172A' }}>
                  4. Data Processor vs Data Controller
                </h3>
                <p className="mb-4" style={{ color: '#334155' }}>
                  Superhost CRM operates as a <strong>Data Processor</strong> for business data uploaded by its users.
                </p>
                <p className="mb-4" style={{ color: '#334155' }}>
                  The User (Hotel / Property Owner / Business Customer) acts as the <strong>Data Controller</strong>.
                </p>
                <p className="mb-6" style={{ color: '#334155' }}>
                  Users must ensure they have the legal right to upload guest data.
                </p>

                <h3 className="text-lg font-semibold mb-4" style={{ color: '#0F172A' }}>
                  5. Data Storage and Security
                </h3>
                <p className="mb-4" style={{ color: '#334155' }}>
                  Data is stored on secure cloud infrastructure such as AWS or Google Cloud.
                </p>
                <p className="mb-4" style={{ color: '#334155' }}>
                  Security measures include HTTPS encryption, TLS 1.2+, encrypted databases and role-based access control.
                </p>
                <p className="mb-6" style={{ color: '#334155' }}>
                  Servers may be located in India or international cloud regions.
                </p>

                <h3 className="text-lg font-semibold mb-4" style={{ color: '#0F172A' }}>
                  6. Cookies
                </h3>
                <p className="mb-4" style={{ color: '#334155' }}>
                  Superhost CRM uses cookies to maintain login sessions and improve functionality.
                </p>
                <p className="mb-6" style={{ color: '#334155' }}>
                  Users can disable cookies in their browser settings.
                </p>

                <h3 className="text-lg font-semibold mb-4" style={{ color: '#0F172A' }}>
                  7. Third-Party Services
                </h3>
                <p className="mb-6" style={{ color: '#334155' }}>
                  The platform may integrate with services such as WhatsApp APIs, cloud hosting providers, payment gateways and analytics tools.
                </p>

                <h3 className="text-lg font-semibold mb-4" style={{ color: '#0F172A' }}>
                  8. Compliance with Privacy Laws
                </h3>
                <p className="mb-4" style={{ color: '#334155' }}>
                  Superhost CRM complies with Digital Personal Data Protection Act (DPDPA) 2023 India.
                </p>
                <p className="mb-4" style={{ color: '#334155' }}>
                  Where applicable we follow GDPR principles.
                </p>
                <p className="mb-6" style={{ color: '#334155' }}>
                  Users may request access, correction or deletion of their data.
                </p>

                <h3 className="text-lg font-semibold mb-4" style={{ color: '#0F172A' }}>
                  9. Data Breach Notification
                </h3>
                <p className="mb-6" style={{ color: '#334155' }}>
                  In the event of a data breach we will investigate immediately and notify affected users within 72 hours when legally required.
                </p>
              </section>

              {/* Terms & Conditions Section */}
              <section ref={sectionRefs.terms} id="terms" className="mb-12 pt-8 border-t border-gray-100 scroll-mt-40">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: '#EEF2FF' }}>
                    <FileText size={24} style={{ color: '#6366F1' }} />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold" style={{ color: '#0F172A' }}>
                    10. Terms & Conditions
                  </h2>
                </div>
                
                <p className="mb-4" style={{ color: '#334155' }}>
                  Users must use Superhost CRM responsibly and legally.
                </p>
                <p style={{ color: '#334155' }}>
                  Accounts may be suspended if policies are violated.
                </p>
              </section>

              {/* Refund Policy Section */}
              <section ref={sectionRefs.refund} id="refund" className="mb-12 pt-8 border-t border-gray-100 scroll-mt-40">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: '#FEF3C7' }}>
                    <RefreshCw size={24} style={{ color: '#F59E0B' }} />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold" style={{ color: '#0F172A' }}>
                    11. Refund Policy
                  </h2>
                </div>
                
                <p className="mb-4" style={{ color: '#334155' }}>
                  Payments may include subscription fees, setup fees and service charges.
                </p>
                <p className="mb-4" style={{ color: '#334155' }}>
                  Refunds may be issued only if payment was made in error or service was not activated.
                </p>
                <p style={{ color: '#334155' }}>
                  Refund requests must be submitted within 7 days.
                </p>
              </section>

              {/* Data Deletion Policy Section */}
              <section ref={sectionRefs.deletion} id="deletion" className="mb-12 pt-8 border-t border-gray-100 scroll-mt-40">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: '#FEE2E2' }}>
                    <Trash2 size={24} style={{ color: '#EF4444' }} />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold" style={{ color: '#0F172A' }}>
                    12. Data Deletion Policy
                  </h2>
                </div>
                
                <p className="mb-4" style={{ color: '#334155' }}>
                  Users may request account deletion.
                </p>
                <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: '#F8FAFC' }}>
                  <p className="font-semibold mb-2" style={{ color: '#0F172A' }}>Contact:</p>
                  <p style={{ color: '#334155' }}>
                    <a href="mailto:support@getroombookings.com" className="hover:underline" style={{ color: '#10B981' }}>
                      support@getroombookings.com
                    </a>
                  </p>
                  <p style={{ color: '#334155' }}>
                    <a href="tel:+919310651733" className="hover:underline" style={{ color: '#10B981' }}>
                      +91 9310651733
                    </a>
                  </p>
                </div>
                <p style={{ color: '#334155' }}>
                  Data will be removed from production systems after verification.
                </p>
              </section>

              {/* Policy Updates */}
              <section className="mb-12 pt-8 border-t border-gray-100">
                <h2 className="text-xl sm:text-2xl font-bold mb-4" style={{ color: '#0F172A' }}>
                  13. Policy Updates
                </h2>
                <p style={{ color: '#334155' }}>
                  Policies may be updated periodically.
                </p>
              </section>

              {/* Contact */}
              <section className="pt-8 border-t border-gray-100">
                <h2 className="text-xl sm:text-2xl font-bold mb-4" style={{ color: '#0F172A' }}>
                  14. Contact
                </h2>
                <div className="p-6 rounded-lg" style={{ backgroundColor: '#F8FAFC' }}>
                  <p className="font-bold text-lg mb-3" style={{ color: '#0F172A' }}>Superhost CRM</p>
                  <p className="mb-2">
                    <a href="mailto:support@getroombookings.com" className="hover:underline" style={{ color: '#10B981' }}>
                      support@getroombookings.com
                    </a>
                  </p>
                  <p>
                    <a href="tel:+919310651733" className="hover:underline" style={{ color: '#10B981' }}>
                      +91 9310651733
                    </a>
                  </p>
                </div>
              </section>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 py-8">
          <div className="max-w-[900px] mx-auto px-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm" style={{ color: '#334155' }}>
                © 2026 Superhost CRM
              </p>
              <div className="flex items-center gap-6">
                <button 
                  onClick={() => scrollToSection('privacy')}
                  className="text-sm hover:underline"
                  style={{ color: '#334155' }}
                >
                  Privacy Policy
                </button>
                <button 
                  onClick={() => scrollToSection('terms')}
                  className="text-sm hover:underline"
                  style={{ color: '#334155' }}
                >
                  Terms
                </button>
                <button 
                  onClick={() => scrollToSection('refund')}
                  className="text-sm hover:underline"
                  style={{ color: '#334155' }}
                >
                  Refund Policy
                </button>
                <a 
                  href="mailto:support@getroombookings.com"
                  className="text-sm hover:underline"
                  style={{ color: '#334155' }}
                >
                  Contact
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Custom scrollbar hide */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </>
  );
};

export default LegalPolicyPage;
